import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { mergeMap, Observable, of, throwError } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';
import { Contract, ContractHelper, PortfolioService, UrlHelper } from '@ecm/portfolio';

import { ErrorPageService } from '../../../services/error-page.service';
import { RouterService } from '../../../services/router.service';

export abstract class AbstractPortfolioResolver {
    // force display contract selector
    protected force: boolean;

    protected constructor(
        protected errorPageService: ErrorPageService,
        protected portfolioService: PortfolioService,
        protected routerService: RouterService,
    ) {}

    /**
     * Check if CID is present in route and CID belongs to user portfolio
     * @param {ActivatedRouteSnapshot} activatedRouteSnapshot
     * @param {RouterStateSnapshot} routerStateSnapshot
     * @param {string} [filter] : named filter or telco/security. Will be used as query param to get the contracts
     * @returns {Observable<boolean>}
     */
    protected checkContract(
        activatedRouteSnapshot: ActivatedRouteSnapshot,
        routerStateSnapshot: RouterStateSnapshot,
        filter?: string,
    ): Observable<boolean> {
        // get current url
        const url: string = routerStateSnapshot.url;

        // perform cid regex on url
        const cidMatch: string[] = UrlHelper.matchCid(url) || UrlHelper.matchSecurityCid(url);

        if (cidMatch) {
            // use second match (first match can't be used because it captures slashes arround cid)
            const cid: string = cidMatch[1];

            return this.portfolioService.getContractByCid(cid, filter).pipe(
                mergeMap((contract: Contract) => {
                    if (contract) {
                        if (ContractHelper.isFictifContract(contract)) {
                            // don't allow FICTIF (BAL secondaire)
                            this.force = true;
                            return throwError(
                                () => `${cid} is a FICTIF (BAL secondaire) contract, will display selector page`,
                            );
                        } else {
                            EcmDispatcherModule.sendConsoleInfo(
                                AbstractPortfolioResolver.name,
                                `${cid} matches portfolio, will let go to route ${url}`,
                            );

                            // set coookie
                            this.portfolioService.setContractCookie(contract);

                            return of(true);
                        }
                    } else {
                        this.force = true;
                        return throwError(() => `${cid} doesn't match portfolio, will display selector page`);
                    }
                }),
            );
        } else {
            return throwError(() => `No CID provided in route ${url}, will display selector page`);
        }
    }
}
